import { Alert, Box, Button, Link, Popover, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useVoyageData } from '../../../store';
import useDateFormat from '../../../../../shared/hooks/useDateFormat';
import useUserRoles from '../../../../../shared/hooks/useUserRoles';

/**
 * Shared alert banner for TBN (unmapped) voyages.
 *
 * @param {"warning"|"info"} severity – MUI alert severity
 * @param {React.ReactNode} message  – custom alert body; receives `tbnCount`
 * @param {(voyage: object) => void} [onAddShip] – callback when user clicks Add Ship for a row
 */
const TbnAlert = ({ severity = 'info', message, onAddShip }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const { tbnVoyages } = useVoyageData();
  const { formatDate } = useDateFormat();
  const { isAdmin, isKeyUser } = useUserRoles();
  const canAddShip = onAddShip && (isAdmin || isKeyUser);

  const tbnCount = tbnVoyages.length;
  if (tbnCount === 0) return null;

  const defaultMessage = (
    <>
      TBN placeholder used in {tbnCount} {tbnCount === 1 ? 'voyage' : 'voyages'}
      . Please Update.
    </>
  );

  return (
    <>
      <Alert severity={severity}>
        {message ? message(tbnCount) : defaultMessage}{' '}
        <Link
          component='button'
          variant='body2'
          underline='always'
          sx={{ fontWeight: 600, verticalAlign: 'baseline' }}
          onClick={(e) => setAnchorEl(e.currentTarget)}
        >
          More Details
        </Link>
      </Alert>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        marginThreshold={16}
        slotProps={{
          paper: { sx: { p: 2, maxWidth: canAddShip ? 780 : 650 } },
        }}
      >
        <Typography variant='subtitle2' sx={{ mb: 1, fontWeight: 700 }}>
          Unmapped Voyages (IMO = TBN)
        </Typography>
        <Box
          component='table'
          sx={{
            width: '100%',
            borderCollapse: 'collapse',
            '& th, & td': {
              px: 1,
              py: 0.5,
              fontSize: '0.75rem',
              whiteSpace: 'nowrap',
              textAlign: 'left',
            },
            '& th': {
              fontWeight: 700,
              borderBottom: 1,
              borderColor: 'divider',
            },
            '& tr:not(:last-child) td': {
              borderBottom: 1,
              borderColor: 'divider',
            },
          }}
        >
          <thead>
            <tr>
              <th>ETD</th>
              <th>ETA</th>
              <th>Departure</th>
              <th>Destination</th>
              <th>Remarks</th>
              {canAddShip && <th>Action</th>}
            </tr>
          </thead>
          <tbody>
            {tbnVoyages.map((v) => (
              <tr key={v.id}>
                <td>{formatDate(v.actualETD) || '—'}</td>
                <td>{formatDate(v.actualETA) || '—'}</td>
                <td>
                  {v.departurePort?.unLocode ?? '—'}{' '}
                  <Typography
                    component='span'
                    sx={{ color: 'text.secondary', fontSize: '0.7rem' }}
                  >
                    {v.departurePort?.name ?? ''}
                  </Typography>
                </td>
                <td>
                  {v.destinationPort?.unLocode ?? '—'}{' '}
                  <Typography
                    component='span'
                    sx={{ color: 'text.secondary', fontSize: '0.7rem' }}
                  >
                    {v.destinationPort?.name ?? ''}
                  </Typography>
                </td>
                <td
                  style={{
                    maxWidth: 160,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {v.generalRemarks || '—'}
                </td>
                {canAddShip && (
                  <td>
                    <Button
                      size='small'
                      variant='text'
                      onClick={() => onAddShip(v)}
                      sx={{
                        fontSize: '0.7rem',
                        textTransform: 'none',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Add Ship
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </Box>
      </Popover>
    </>
  );
};

export default TbnAlert;
